import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type { ChuttamalleQualityId } from "./chuttamalle-preset";

/**
 * /chuttamalle-ai-video-generator 样片区的片子，做法同 hotel-lobby-showcase.ts。
 *
 * 2026-10-09 站内出片（FacelessReels 那页没有舞蹈视频可扒，老公定改站内出）：gpt-image-2
 * 出 3 对虚构人物，/image-to-video 参考图模式用本页同一条 prompt、9:16、5 秒，
 * 1 条 Lite + 2 条 Standard。成片带模型生成的原创配乐，不是原曲。来源、积分和压制参数见
 * packages/scripts/chuttamalle-showcase/README.md。站内出的 onSite = true，进 VideoObject。
 *
 * R2 `home/showcase/chuttamalle/` 下 clips/{id}.mp4、posters/{id}.jpg、refs/{id}-left.jpg / -right.jpg。
 * 清单为空时样片区整个不渲染。
 */
export interface ChuttamalleShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档，拼贴 hover 时显示档位名 */
	quality: ChuttamalleQualityId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 左右两张原图（R2 refs/{id}-left.jpg / -right.jpg），「Try this example」填进 hero 的两个槽 */
	inputImages: [string, string];
}

const R2_BASE = "home/showcase/chuttamalle";

/**
 * 视频地址的版本参数。assets 域的 GET 走 Cloudflare 边缘缓存（max-age 一天），同一个 key
 * 覆盖上传后一天内还是旧文件；换片子时把这个数字加一
 */
const CLIP_VERSION = 1;

type Spec = Omit<
	ChuttamalleShowcaseClip,
	"videoUrl" | "poster" | "inputImages"
>;

/** 顺序即拼贴顺序：3 列、每列一条 */
const SPECS: Spec[] = [
	{
		id: "saree-kurta",
		title: "Saree & Kurta",
		description:
			"A woman in a red silk saree and a man in a white kurta sway, twirl and finish shoulder to shoulder on a sunset beach.",
		quality: "lite",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
	},
	{
		id: "sundress-linen",
		title: "Sundress & Linen",
		description:
			"A woman in a yellow sundress and a man in a blue linen shirt mirror each other's moves at sunset and end side by side.",
		quality: "standard",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
	},
	{
		id: "salwar-nehru",
		title: "Salwar & Nehru",
		description:
			"A couple in an emerald salwar kameez and a navy Nehru jacket sway, turn and finish side by side on a sunset beach.",
		quality: "standard",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
	},
];

export const chuttamalleShowcaseClips: ChuttamalleShowcaseClip[] = SPECS.map(
	(spec) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(`${R2_BASE}/posters/${spec.id}.jpg`),
		inputImages: [
			buildR2AssetsImageUrl(`${R2_BASE}/refs/${spec.id}-left.jpg`),
			buildR2AssetsImageUrl(`${R2_BASE}/refs/${spec.id}-right.jpg`),
		],
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const CHUTTAMALLE_SHOWCASE_UPLOADED_AT = "2026-10-09T00:00:00+08:00";
